import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fmtTRY } from "@/lib/api";
import { ArrowRightLeft } from "lucide-react";

export default function TransferDialog({ open, onOpenChange, accounts, onSaved }) {
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setFromId("");
    setToId("");
    setAmount("");
    setDescription("");
    setDate(new Date().toISOString().slice(0, 10));
  };

  const submit = async () => {
    if (!fromId || !toId) return toast.error("Kaynak ve hedef hesap seçin");
    if (fromId === toId) return toast.error("Hesaplar aynı olamaz");
    if (!amount || Number(amount) <= 0) return toast.error("Geçerli bir tutar girin");
    setSaving(true);
    try {
      await api.post("/transactions", {
        type: "transfer",
        amount: Number(amount),
        description,
        date,
        account_id: fromId,
        to_account_id: toId,
      });
      toast.success("Transfer tamamlandı");
      reset();
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bir hata oluştu");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-blue-600" /> Para Transferi
          </DialogTitle>
          <DialogDescription>Hesaplar arasında para aktarın.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Kaynak Hesap</Label>
            <Select value={fromId} onValueChange={setFromId}>
              <SelectTrigger data-testid="transfer-from-select">
                <SelectValue placeholder="Gönderen hesap" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name} · {fmtTRY(a.balance)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Hedef Hesap</Label>
            <Select value={toId} onValueChange={setToId}>
              <SelectTrigger data-testid="transfer-to-select">
                <SelectValue placeholder="Alan hesap" />
              </SelectTrigger>
              <SelectContent>
                {accounts
                  .filter((a) => a.id !== fromId)
                  .map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name} · {fmtTRY(a.balance)}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tutar (₺)</Label>
              <Input
                data-testid="transfer-amount-input"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0,00"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Tarih</Label>
              <Input
                data-testid="transfer-date-input"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Açıklama</Label>
            <Textarea
              data-testid="transfer-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Opsiyonel not..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            data-testid="save-transfer-btn"
            onClick={submit}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
          >
            {saving ? "Aktarılıyor..." : "Transferi Onayla"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
